import React from 'react';
import { faChartArea, faPencilAlt, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import IconWithTooltip from 'components/IconWithTooltip/IconWithTooltip';
import TableRowToolBar from 'containers/TableRowToolBar/TableRowToolBar';
import { Link } from 'react-router-dom';
import styles from './l1ObjectList.module.scss';
import i18n from 'i18n';
import _ from 'lodash';
import { L1ObjectState, L1Object, L1ObjectChannel, L2ObjectRunningStatus } from 'core/l1Object/L1Object';
import { Status } from 'components/Status/Status';
import PermissionChecker from 'containers/PermissionChecker/PermissionChecker';
import { RoleNames } from 'core/auth/Account';
import { hasFuncs, notRoles, notSettleOrder } from 'core/permission/PermissionDSL';
import { Permission } from 'core/auth/Permission';
import moment from 'moment';
import Progress from 'components/Progress/Progress';
import { Currency } from 'core';
import { Order, State } from 'core/order/Order';

export const getRtbCampaignGroupStatusDesData = (l1Object: L1Object, order: Order) => {
  let des = '';
  let color: any;
  const budget = l1Object.budget ? l1Object.budget : 0;
  const spent = _.get(l1Object, 'spent', 0);
  switch (l1Object.state) {
    case L1ObjectState.DEACTIVATE:
      des = i18n.t<string>('l1ObjectList.labels.deactivateState');
      color = 'black';
      break;
    case L1ObjectState.DELETE:
      des = i18n.t<string>('l1ObjectList.labels.deleteState');
      color = 'danger';
      break;
    case L1ObjectState.ACTIVE:
      if (moment().isAfter(moment(order.endDate).endOf('day'))) {
        des = i18n.t<string>('l1ObjectList.labels.endState');
        color = 'light';
        break;
      }
      if (!l1Object.l2ObjectMinStartDate && !l1Object.l2ObjectMaxEndDate) {
        des = i18n.t<string>('l1ObjectList.labels.notStartState');
        color = 'light';
      } else if (moment().isBefore(moment(_.get(l1Object, 'l2ObjectMinStartDate')))) {
        des = i18n.t<string>('l1ObjectList.labels.notStartState');
        color = 'light';
      } else if (moment().isAfter(moment(_.get(l1Object, 'l2ObjectMaxEndDate'))) && (spent >= +budget)) {
        des = i18n.t<string>('l1ObjectList.labels.endState');
        color = 'light';
      } else {
        des = i18n.t<string>('l1ObjectList.labels.activeState');
      }
      break;
    default:
      des = i18n.t<string>('l1ObjectList.labels.activeState');
      break;
  }

  return {
    des,
    color
  };
};

export const getRtbCampaignGroupDeliveryDesDatas = (l1Object: L1Object) => {
  const deliveries = _.omit(l1Object.l2ObjectRunningStatus, [
    L2ObjectRunningStatus.BUDGET_NOT_ALLOCATED,
    L2ObjectRunningStatus.BUDGET_REMAIN,
    L2ObjectRunningStatus.ACTUAL_PROGRESS,
    L2ObjectRunningStatus.EXPECTED_PROGRESS,
    L2ObjectRunningStatus.BUDGET,
    L2ObjectRunningStatus.SPENT
  ]);
  const sortedDeliveries = _.keys(deliveries)
    .filter(key => deliveries[key] as number > 0)
    .sort((dataA, dataB) => {
      const stateDes: string[] = [
        L2ObjectRunningStatus.AHEAD,
        L2ObjectRunningStatus.SEVERELY_BEHIND,
        L2ObjectRunningStatus.BEHIND,
        L2ObjectRunningStatus.NO_CREATIVES,
        L2ObjectRunningStatus.NOT_START,
        L2ObjectRunningStatus.NORMAL,
        L2ObjectRunningStatus.INACTIVE,
        L2ObjectRunningStatus.END,
        L2ObjectRunningStatus.PARTIALLY_OUT_OF_STOCK,
        L2ObjectRunningStatus.FULLY_OUT_OF_STOCK,
        L2ObjectRunningStatus.SEGMENT_RULE_STRICT,
        L2ObjectRunningStatus.BELOW_MIN_BUDGET_COUNT,
        L2ObjectRunningStatus.CAMPAIGN_TYPE_OFF_COUNT,
        L2ObjectRunningStatus.PRODUCT_AUTH_REMOVED_COUNT
      ];
      const dataAToCompare = stateDes.indexOf(dataA);
      const dataBToCompare = stateDes.indexOf(dataB);
      return dataAToCompare > dataBToCompare ? 1 : -1;
    });

  const behindAdTypes = _.defaultTo(
    l1Object.l2ObjectRunningStatus.behindAdTypes,
    []
  );
  const i18nBehindAdTypeKey = _.camelCase(behindAdTypes.sort().join('_'));
  const behindExtraInfo =
    behindAdTypes.length > 0
      ? i18n.t<string>(
          `l1ObjectList.labels.delivery.${i18nBehindAdTypeKey}.behindExtraInfo`
        )
      : '';
  const severelyBehindAdTypes = _.defaultTo(
    l1Object.l2ObjectRunningStatus.severelyBehindAdTypes,
    []
  );
  const i18nSeverelyBehindAdTypeKey = _.camelCase(
    severelyBehindAdTypes.sort().join('_')
  );
  const severelyBehindExtraInfo =
    severelyBehindAdTypes.length > 0
      ? i18n.t<string>(
          `l1ObjectList.labels.delivery.${i18nSeverelyBehindAdTypeKey}.behindExtraInfo`
        )
      : '';
  return _.map(sortedDeliveries, (key) => {
    let des = '';
    let color: string | undefined = undefined;
    let extraInfo: string | undefined = undefined;

    switch (key) {
      case L2ObjectRunningStatus.NOT_START:
        des = i18n.t<string>('l1ObjectList.labels.delivery.notStart');
        color = 'light';
        break;
      case L2ObjectRunningStatus.STOPPING:
        des = i18n.t<string>('campaignList.labels.stoppingState');
        color = 'whiteTheme4';
        break;
      case L2ObjectRunningStatus.BID_RPICE_LOWER_THAN_FLOOR_COUNT:
        des = i18n.t<string>('campaignList.labels.bidPriceLowerThanFloor');
        color = 'danger';
        break;
      case L2ObjectRunningStatus.NO_CREATIVES:
        des = i18n.t<string>('l1ObjectList.labels.delivery.noCreatives');
        color = 'black';
        break;
      case L2ObjectRunningStatus.NORMAL:
        des = i18n.t<string>('l1ObjectList.labels.delivery.normal');
        color = 'success';
        break;
      case L2ObjectRunningStatus.BEHIND:
        des = i18n.t<string>('l1ObjectList.labels.delivery.behind');
        color = 'warning';
        extraInfo = behindExtraInfo;
        break;
      case L2ObjectRunningStatus.SEVERELY_BEHIND:
        des = i18n.t<string>('l1ObjectList.labels.delivery.severelyBehind');
        color = 'deep-warning';
        extraInfo = severelyBehindExtraInfo;
        break;
      case L2ObjectRunningStatus.AHEAD:
        des = i18n.t<string>('l1ObjectList.labels.delivery.ahead');
        color = 'danger';
        break;
      case L2ObjectRunningStatus.INACTIVE:
        des = i18n.t<string>('l1ObjectList.labels.delivery.inactive');
        color = 'black';
        break;
      case L2ObjectRunningStatus.END:
        des = i18n.t<string>('l1ObjectList.labels.delivery.end');
        color = 'light';
        break;
      case L2ObjectRunningStatus.PARTIALLY_OUT_OF_STOCK:
        des = i18n.t<string>('l1ObjectList.labels.delivery.outOfStock');
        color = 'warning';
        break;
      case L2ObjectRunningStatus.FULLY_OUT_OF_STOCK:
        des = i18n.t<string>('l1ObjectList.labels.delivery.outOfStock');
        color = 'deep-warning';
        break;
      case L2ObjectRunningStatus.SEGMENT_RULE_STRICT:
        des = i18n.t<string>('l1ObjectList.labels.delivery.segmentRuleStrict');
        color = 'danger';
        break;
      case L2ObjectRunningStatus.CAMPAIGN_TYPE_OFF_COUNT:
        des = i18n.t<string>('l1ObjectList.labels.delivery.adTypeDisabled');
        color = 'danger';
        break;
      case L2ObjectRunningStatus.BELOW_MIN_BUDGET_COUNT:
        des = i18n.t<string>('l1ObjectList.labels.delivery.budgetNotEnough');
        color = 'danger';
        break;
      case L2ObjectRunningStatus.PRODUCT_AUTH_REMOVED_COUNT:
        des = i18n.t<string>('campaignList.labels.productNotAuthorized');
        color = 'danger';
        break;
      default:
        break;
    }

    return {
      des,
      color,
      value: deliveries[key],
      extraInfo
    };
  });
};

const nameHeaderFormatter = (
  editableL1ObjectLength: number,
  selectedL1Objects: (number | string)[],
  order,
  onSelectAll,
  column: any,
  _1,
  { sortElement }
) => {
  return (
    <div className={styles.nameHeader}>
      <div className={styles.selectCheckBox}>
        {editableL1ObjectLength > 0 &&
          <PermissionChecker permissionAware={hasFuncs(Permission.CAMPAIGN_WRITE).and(notSettleOrder(order))}>
            <input
              type='checkbox'
              checked={editableL1ObjectLength === selectedL1Objects.length}
              ref={el => el && (el.indeterminate = editableL1ObjectLength !== selectedL1Objects.length && selectedL1Objects.length > 0)}
              onChange={onSelectAll}
              id={'input0'}
            />
            <label htmlFor='input0' />
          </PermissionChecker>
        }
      </div>
      {i18n.t<string>(column.text)}
      {sortElement}
    </div>
  );
};

const nameFormatter = (l1ObjectId: any, l1Object, _2, formatExtraData: any): any => {
  const { selectedL1Objects, onSelect, permissionAwareGetter } = formatExtraData;
  const l1ObjectDetailReadPermission = notRoles(RoleNames.agencyReport);
  const renderLabel = () => <span className={styles.name}>{l1Object.name}</span>;
  return (
    <div className={styles.nameCell}>
      <div className={styles.selectCheckBox}>
        {l1Object.state !== L1ObjectState.DELETE &&
          <PermissionChecker permissionAware={permissionAwareGetter(l1Object)}>
            <input
              type='checkbox'
              checked={selectedL1Objects.indexOf(l1ObjectId.toString()) > -1}
              onChange={_.partial(onSelect, l1ObjectId.toString())}
              id={`input${l1ObjectId}`}
            />
            <label htmlFor={`input${l1ObjectId}`} />
          </PermissionChecker>
        }
      </div>
      <div className={styles.info}>
        <PermissionChecker permissionAware={l1ObjectDetailReadPermission} renderOtherwise={renderLabel}>
          {l1Object.state !== L1ObjectState.DELETE ?
            <Link to={`${formatExtraData.currentUrl}/campaign-groups/${l1ObjectId}`} className={styles.name}>
              {l1Object.name}
            </Link> :
            renderLabel()
          }
        </PermissionChecker>
        <div className={styles.id}>
          {'ID: ' + l1ObjectId}
        </div>
      </div>
    </div>
  );
};

const stateFormatter = (state, l1Object: L1Object, _2, formatExtraData: any) => {
  const { order } = formatExtraData;
  const channelStatusDesMap = {
    [L1ObjectChannel.RTB]: () => getRtbCampaignGroupStatusDesData(l1Object, order)
  };
  const stateData = channelStatusDesMap[l1Object.channel] ? channelStatusDesMap[l1Object.channel]() : {
    des: state,
    color: 'black'
  };

  return (
    <Status
      label={stateData.des}
      color={stateData.color}
    />
  );
};

const deliveryFormatter = (state: string, l1Object: L1Object, _2, formatExtraData: any) => {
  const progressData = formatExtraData(l1Object);
  const showProgress = progressData;
  const channelStatusDesMap = {
    [L1ObjectChannel.RTB]: () => getRtbCampaignGroupDeliveryDesDatas(l1Object)
  };
  const stateData = channelStatusDesMap[l1Object.channel] ? channelStatusDesMap[l1Object.channel]() : [{
    des: state,
    color: 'black'
  }];

  return (
    <div className={styles.deliveryCell}>
      {showProgress &&
        <div className={styles.progressCell}>
          <div className={styles.progressContent}>
            <div className={styles.progress}>
              <Progress
                progress={progressData.executeRate}
                danger={progressData.danger}
                deepWarning={progressData.deepWarning}
                warning={progressData.warning}
              />
            </div>
            <span>
              {`${(progressData.executeRate * 100).toFixed(2)}% / ${(progressData.predictRate * 100).toFixed(2)}%`}
            </span>
          </div>
        </div>
      }
      {!_.isEmpty(stateData) &&
        <div className={styles.tagsCell}>
          {stateData.map(data => {
            const label = _.get(data, 'value') ? `${data.des} ${_.get(data, 'value')}` : data.des;

            return (
              <Status
                key={data.des}
                label={label}
                color={data.color}
                extraInfoClassName={styles.deliveryStatusOverlay}
                renderExtraInfo={data.extraInfo ? () => data.extraInfo : undefined}
              />
            );
          })}
        </div>
      }
    </div>
  );
};

const floatingEditBtnsFormatter = (_1, l1Object, _2, formatExtraData: any): any => {
  const onDeleteBtnClick = () => formatExtraData.onDeleteBtnClick(l1Object.l1ObjectId);
  const canDelete = _.get(l1Object, 'campaignAmount', 0) === 0;
  const canNotEditState = [State.NOT_APPROVE, State.REJECT, State.SETTLE, State.SETTLED, State.CHANGE_PENDING];
  const canEdit = l1Object.state !== L1ObjectState.DELETE;
  const cannotEditMessage = canNotEditState.includes(formatExtraData.order.state) ? i18n.t<string>('l1Object.labels.orderStateCannotEdit') : undefined;
  const editMessage = _.defaultTo(cannotEditMessage, i18n.t<string>('l1Object.labels.editTitle'));
  const from = encodeURIComponent(moment(l1Object.l2ObjectMinStartDate).startOf('day').format('YYYY-MM-DD HH:mm:ss'));
  const to = encodeURIComponent(moment(l1Object.l2ObjectMaxEndDate).startOf('day').format('YYYY-MM-DD HH:mm:ss'));
  const reportUrl = `/reports/performance?dimension=l1ObjectId&from=${from}&to=${to}&l1ObjectId=${l1Object.l1ObjectId}`;
  return (
    <TableRowToolBar className={styles.floatingEditArea}>
      {canEdit &&
        <PermissionChecker permissionAware={formatExtraData.permissionAwareGetter(l1Object)}>
          <IconWithTooltip
            disabled={!!cannotEditMessage}
            icon={faPencilAlt}
            tooltipProps={{
              id: `campaignGroupList-editTip-${l1Object.l1ObjectId}`,
              tooltip: editMessage,
              link: `${formatExtraData.currentUrl}/campaign-groups/${l1Object.l1ObjectId}/edit`
            }}
          />
        </PermissionChecker>
      }
      {l1Object.state !== L1ObjectState.DELETE &&
        <PermissionChecker permissionAware={hasFuncs(Permission.CAMPAIGN_DEL)}>
          <IconWithTooltip
            disabled={!canDelete}
            icon={faTrashAlt}
            onClick={onDeleteBtnClick}
            tooltipProps={{
              id: `campaignGroupList-deleteTip-${l1Object.l1ObjectId}`,
              tooltip: canDelete ? i18n.t<string>('l1Object.labels.deleteTitle') : i18n.t<string>('l1Object.labels.cannotDeleteTitle')
            }}
          />
        </PermissionChecker>
      }
      <PermissionChecker permissionAware={hasFuncs(Permission.REPORT_ADS)}>
        <IconWithTooltip
          icon={faChartArea}
          tooltipProps={{
            id: `campaignListReportTip-${l1Object.l1ObjectId}`,
            link: reportUrl,
            tooltip: i18n.t<string>('campaignList.labels.reportHint')
          }}
        />
      </PermissionChecker>
    </TableRowToolBar>
  );
};

const channelFormatter = (_1, campaginGroup: L1Object) => {
  const channel = _.get(campaginGroup, 'channel');

  return (
    <div>{i18n.t<string>(`l1Object.labels.channel_${channel.toLowerCase()}`)}</div>
  );
};

const budgetFormatter = (
  getBudgetValue: (budget: any, currency: any) => string,
  currency: Currency,
  value: any,
  l1Object: L1Object
) => {
  const budgetValue = getBudgetValue(value, currency);
  const budgetInfo = _.pick(l1Object.l2ObjectRunningStatus, [
    L2ObjectRunningStatus.BUDGET_REMAIN,
    L2ObjectRunningStatus.BUDGET_NOT_ALLOCATED
  ]);

  const needAlertStatus = _.keys(budgetInfo)
    .filter(key => budgetInfo[key] as number > 0 || budgetInfo[key] as boolean)
    .reduce((acc: any[], key) => {
      let stateData = {
        des: '',
        color: 'black'
      };
      switch (key) {
        case L2ObjectRunningStatus.BUDGET_REMAIN:
          stateData.des = `${i18n.t<string>('l1ObjectList.labels.remainBudget')} ${budgetInfo[key]}`;
          stateData.color = 'danger';
          break;
        case L2ObjectRunningStatus.BUDGET_NOT_ALLOCATED:
          stateData.des = i18n.t<string>('l1ObjectList.labels.undistributedBudget');
          stateData.color = 'danger';
          break;
        default:
          break;
      }
      return [
        ...acc,
        stateData
      ];
    }, []);

  return (
    <div className={styles.budgetCell}>
      {budgetValue}
      {!_.isEmpty(needAlertStatus) &&
        <div className={styles.tagsCell}>
          {needAlertStatus.map(status => (
          <Status
            key={status.des}
            label={status.des}
            color={status.color}
          />
        ))}
        </div>
      }
    </div>
  );
};

const formatters = {
  nameHeaderFormatter,
  nameFormatter,
  stateFormatter,
  deliveryFormatter,
  budgetFormatter,
  floatingEditBtnsFormatter,
  channelFormatter
};
export default formatters;
